import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import './index.css';
import { safeLocalStorage, safeSessionStorage } from './utils/storage';

// Early Multi-Platform WebApp Initialization — Dismiss native splash screen on Telegram & MAX
(function ensurePlatformReady(attempts = 0) {
  try {
    if (typeof window !== 'undefined') {
      const win = window as any;
      try {
        if (win.Telegram?.WebApp) {
          win.Telegram.WebApp.ready?.();
          win.Telegram.WebApp.expand?.();
        }
      } catch {}
      try {
        if (win.WebApp) {
          win.WebApp.ready?.();
          win.WebApp.expand?.();
          win.WebApp.enableVerticalSwipes?.();
        }
      } catch {}
      try {
        win.WebViewHandler?.postEvent?.('WebAppReady', '{}');
        win.WebViewHandler?.postEvent?.('WebAppExpand', '{}');
      } catch {}
      try {
        win.webkit?.messageHandlers?.WebViewHandler?.postMessage?.(JSON.stringify({ type: 'WebAppReady' }));
        win.webkit?.messageHandlers?.webAppReady?.postMessage?.('{}');
      } catch {}
      try {
        window.parent?.postMessage?.(JSON.stringify({ type: 'WebAppReady' }), '*');
      } catch {}
    }
  } catch (e) {
    console.warn('[startup] Failed early SDK initialization:', e);
  }
  if (attempts < 80) {
    setTimeout(() => ensurePlatformReady(attempts + 1), 40);
  }
})();

// Auto-reload on Vite chunk skew (when server deploys new bundle and old cached chunks are gone)
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (event) => {
    console.warn('[Vite] Preload error detected, reloading page to fetch latest version assets...', event);
    const reloadKey = 'last_chunk_reload';
    const lastReload = Number(sessionStorage.getItem(reloadKey) || 0);
    if (Date.now() - lastReload > 10000) {
      sessionStorage.setItem(reloadKey, String(Date.now()));
      window.location.reload();
    }
  });
}

// Normalize initial pathname if opening via /index.html to ensure React Router compatibility
try {
  if (typeof window !== 'undefined' && window.location.pathname.endsWith('/index.html')) {
    const cleanPath = window.location.pathname.replace(/\/index\.html$/, '/') || '/';
    window.history.replaceState(null, '', cleanPath + window.location.search + window.location.hash);
  }
} catch (e) {
  console.warn('[startup] Failed to normalize pathname:', e);
}

// Programmatic IndexedDB & localStorage reset via query parameter
(async () => {
  try {
    if (typeof window !== 'undefined' && window.location.search.includes('reset=true')) {
      if (typeof window.indexedDB !== 'undefined' && typeof window.indexedDB.databases === 'function') {
        try {
          const dbs = await window.indexedDB.databases();
          for (const db of dbs) {
            if (db?.name) {
              window.indexedDB.deleteDatabase(db.name);
            }
          }
        } catch (idbErr) {
          console.warn('[startup] IndexedDB database list unavailable:', idbErr);
        }
      }
      try {
        safeLocalStorage.clear();
        safeSessionStorage.clear();
      } catch (storageErr) {
        console.warn('[startup] Storage clear failed:', storageErr);
      }
      
      const url = new URL(window.location.href);
      url.searchParams.delete('reset');
      window.location.replace(url.pathname + url.search);
    }
  } catch (err) {
    console.error('[startup] Failed to clear database:', err);
  }
})();

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('[startup] Root element #root not found in DOM');
}

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
